// Import necessary dependencies and utilities
import { createApi } from "@reduxjs/toolkit/query/react";
import { customFetchBase } from '../../utils/API/fetchBaseQuery';
import { transformRequest } from "../../utils/API/requestMiddleware";
import { transformErrorResponse, transformSuccessResponse } from "../../utils/API/responseMiddleware";

// Create client API using createApi function from Redux Toolkit
const timeSheetLogAPI = createApi({
    baseQuery: customFetchBase,
    reducerPath: 'timeSheetLogAPI',
    endpoints: (builder) => ({

        // Add Time sheet Log
        addTimeSheetRecord: builder.mutation({
            query: (data) => ({
                url: `/TimeSheet/AddTimesheetRecord`,
                method: 'POST',
                body: transformRequest(data),
            }),

            transformResponse: transformSuccessResponse,
            transformErrorResponse: transformErrorResponse,
        }),

        // Get All Time Sheet Log Records
        getAllTimeSheetRecords: builder.query({
            query: (data) => ({
                url: `/TimeSheet/GetTimesheetDetailsList?StartDate=${data?.startDate}&CurrentUserId=${data?.userId}`,
                method: 'GET',
            }),
            transformResponse: transformSuccessResponse,
            transformErrorResponse: transformErrorResponse,
        }),

        getTimeSheetByUserId: builder.mutation({
            query: (data) => ({
                url: `/TimeSheet/GetTimeSheetByUserId`,
                method: 'POST',
                body: transformRequest(data)
            }),
            transformResponse: transformSuccessResponse,
            transformErrorResponse: transformErrorResponse,
        }),


        getTimeSheetByReviewUserId: builder.mutation({
            query: (data) => ({
                url: `/TimeSheet/GetTimeSheetByReviewUserID`,
                method: 'POST',
                body: transformRequest(data)
            }),
            transformResponse: transformSuccessResponse,
            transformErrorResponse: transformErrorResponse,
        }),

        getTimeSheetLog: builder.mutation({
            query: (data) => ({
                url: `/TimeSheet/GetTimeSheetLogList`,
                method: 'POST',
                body: transformRequest(data)
            }),
            transformResponse: transformSuccessResponse,
            transformErrorResponse: transformErrorResponse,
        }),
        deleteTimesheetDetail: builder.mutation({
            query: (id) => ({
                url: `TimeSheet/DeleteTimesheetDetail?id=${id}`,
                method: 'DELETE',
                body: transformRequest(id)
            }),
            transformResponse: transformSuccessResponse,
            transformErrorResponse: transformErrorResponse,
        }),

        submitTimesheet: builder.mutation({
            query: (data) => ({
                url: `TimeSheet/SubmitTimesheet`,
                method: 'POST',
                body: transformRequest(data)
            }),
            transformResponse: transformSuccessResponse,
            transformErrorResponse: transformErrorResponse,
        }),
        getTimeSheetLogListByTaskId: builder.query({
            query: (taskId) => ({
                url: `/TimeSheet/GetTimeSheetLogListByTaskId?taskId=${taskId}`,
                method: 'GET',
            }),
            transformResponse: transformSuccessResponse,
            transformErrorResponse: transformErrorResponse,
        }),
        getTimeSheetStatus: builder.query({
            query: (data) => ({
                url: `/TimeSheet/GetTimeSheetStatus?StartDate=${data?.startDate}&UserID=${data.userId}`,
                method: 'GET',
            }),
            transformResponse: transformSuccessResponse,
            transformErrorResponse: transformErrorResponse,
        }),
        updateTimesheetStatus: builder.mutation({
            query: (data) => ({
                url: `TimeSheet/UpdateTimesheetStatus`,
                method: 'POST',
                body: transformRequest(data)
            }),
            transformResponse: transformSuccessResponse,
            transformErrorResponse: transformErrorResponse,
        }),
        getWeeklyActivityChart:builder.query({
            query: (data) => ({
                url: `/TimeSheet/GetWeeklyActivityChart?UserID=${data?.userId}&StartDate=${data?.startDate}&EndDate=${data?.endDate}`,
                method: 'GET',
            }),
            transformResponse: transformSuccessResponse,
            transformErrorResponse: transformErrorResponse,
        }),
        getWeeklyProjectChart:builder.query({
            query: (data) => ({
                url: `/TimeSheet/GetWeeklyProjectChart?UserID=${data?.userId}&StartDate=${data?.startDate}&EndDate=${data?.endDate}`,
                method: 'GET',
            }),
            transformResponse: transformSuccessResponse,
            transformErrorResponse: transformErrorResponse,
        }),

         // GetTimesheetListByFilters 
         getTimesheetListByFilters: builder.query({
            query: (data) => ({
                url: `/TimeSheet/GetTimesheetDataListByFilters`,
                method: 'post',
                body: transformRequest(data), 
            }),
            transformResponse: transformSuccessResponse,
            transformErrorResponse: transformErrorResponse,
        }),

        // ExportTimesheetData 
        exportTimesheetData: builder.query({
            query: (data) => ({
                url: `/TimeSheet/ExportTimesheetData`,
                method: 'post',
                body: transformRequest(data), 
                responseHandler: (response) => response.blob(),
            }),
        }),
    
        getTimesheetActivityReportChart: builder.query({
            query: (data) => ({
                url: `/TimeSheet/GetTimesheetActivityReportChart?ProjectId=${data?.projectId}&UserID=${data.userId}`,
                method: 'GET',
            }),
            transformResponse: transformSuccessResponse,
            transformErrorResponse: transformErrorResponse,
        }),

        getTimesheetSubActivityReportChart: builder.query({
            query: (data) => ({
                url: `/TimeSheet/GetTimesheetSubActivityReportChart?ProjectId=${data?.projectId}&UserID=${data.userId}`,
                method: 'GET',
            }),
            transformResponse: transformSuccessResponse,
            transformErrorResponse: transformErrorResponse,
        }),

        getTimesheetActivityReportChartByProject: builder.query({
            query: (data) => ({
                url: `/TimeSheet/GetTimesheetActivityReportChartByProject?ProjectId=${data}`,
                method: 'GET',
            }),
            transformResponse: transformSuccessResponse,
            transformErrorResponse: transformErrorResponse,
        }),
        getTimesheetSubActivityReportChartByProject: builder.query({
            query: (data) => ({
                url: `/TimeSheet/GetTimesheetSubActivityReportChartByProject?ProjectId=${data}`,
                method: 'GET',
            }),
            transformResponse: transformSuccessResponse,
            transformErrorResponse: transformErrorResponse,
        }),
    }),
})

export const {
    useAddTimeSheetRecordMutation,
    useLazyGetAllTimeSheetRecordsQuery,
    useGetTimeSheetByUserIdMutation,
    useGetTimeSheetByReviewUserIdMutation,
    useGetTimeSheetLogMutation,
    useDeleteTimesheetDetailMutation,
    useSubmitTimesheetMutation,
    useLazyGetTimeSheetLogListByTaskIdQuery,
    useLazyGetTimeSheetStatusQuery,
    useUpdateTimesheetStatusMutation,
    useLazyGetWeeklyActivityChartQuery,
    useLazyGetWeeklyProjectChartQuery,
    useLazyGetTimesheetListByFiltersQuery,
    useLazyExportTimesheetDataQuery,
    useLazyGetTimesheetActivityReportChartQuery,
    useLazyGetTimesheetSubActivityReportChartQuery,
    useLazyGetTimesheetActivityReportChartByProjectQuery,
    useLazyGetTimesheetSubActivityReportChartByProjectQuery,
} = timeSheetLogAPI;

export default timeSheetLogAPI;